import axios, { AxiosError } from 'axios';
import type { ApiErrorBody } from './types';

const TOKEN_KEY = 'idverify-token';
const EXPIRY_KEY = 'idverify-token-expires';

/** Reads the persisted bearer token, or null when signed out / expired. */
export function getToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const expires = localStorage.getItem(EXPIRY_KEY);
  if (!token || !expires) return null;

  // Drop the token proactively rather than waiting for the API to reject it.
  if (new Date(expires).getTime() <= Date.now()) {
    clearToken();
    return null;
  }
  return token;
}

export function setToken(token: string, expiresAtIso: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(EXPIRY_KEY, expiresAtIso);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRY_KEY);
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Fired when the API rejects the token. AuthContext subscribes and resets to the login
 * screen, so no component needs to handle 401 individually.
 */
export const UNAUTHORIZED_EVENT = 'idverify:unauthorized';

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      clearToken();
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }
    return Promise.reject(error);
  },
);

/**
 * Turns any thrown value into a message suitable for an alert box. Prefers the API's own
 * `message` field, falls back to the HTTP status, then to a generic string.
 */
export function toErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as ApiErrorBody | undefined;

    // A model-binding failure carries a generic summary plus the field messages that actually say
    // what is wrong. Prefer the first field message: "Enter a valid phone number..." is useful,
    // "One or more fields are invalid." is not.
    const fieldError = body?.errors && Object.values(body.errors).flat().find((m) => !!m);
    if (fieldError) return fieldError;

    if (body?.message) return body.message;

    // Operation responses ({ success: false, error }) carry their reason in `error`.
    const operationError = (body as { error?: string } | undefined)?.error;
    if (operationError) return operationError;

    if (error.response?.status === 403) return 'You do not have access to this resource.';
    if (error.response?.status === 404) return 'Not found.';
    if (!error.response) return 'Cannot reach the server. Check that the API is running.';
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
